import { Separator, Stack } from "@fluentui/react";
import { Card, CardHeader, Text, Tooltip } from "@fluentui/react-components";
import {
  CalendarCheckmarkRegular,
  CalendarPlayRegular,
  FlowchartRegular,
  Form20Regular,
  Info24Regular,
  TargetAddRegular,
} from "@fluentui/react-icons";
import {
  getCompletedInterviews,
  getStartedInterviews,
  getTotalDiagnosticsCreated,
  getTotalDistributionsCreated,
  getTotalObjectivesCreated,
} from "api";
import { findSidebarItem } from "components/adminSidebar/adminSidebarConfig";
import { DateRangeSelector } from "components/manageStatistics/components/DateRangeSelector";
import { useSharedAdminContentHeaderStyles } from "theme/sharedStyles";
import { ChartExportToCSV } from "components/manageStatistics/exporter/ChartExportToCSV";
import { getStartAndEndOfPeriod } from "components/manageStatistics/utils/dateUtils";
import { useState } from "react";
import {
  setGapBetweenHeadersAndDetailsList,
  useStyles,
} from "./ManageInterviewsStatistics.styles";

export const ManageInterviewsStatistics = (): JSX.Element => {
  const headerClasses = useSharedAdminContentHeaderStyles();
  const styles = useStyles();
  const pageTitle =
    findSidebarItem("interviewsStatistics")?.label ?? "Interviews Statistics";
  const [dateRangeType, setDateRangeType] = useState<string>("Day");
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [customStartDate, setCustomStartDate] = useState<Date | undefined>(
    undefined,
  );
  const [customEndDate, setCustomEndDate] = useState<Date | undefined>(
    undefined,
  );
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [customDateError, setCustomDateError] = useState<string | null>(null);
  const [diagnosticsCreated, setDiagnosticsCreated] = useState<
    number | undefined
  >(undefined);
  const [objectivesCreated, setObjectivesCreated] = useState<
    number | undefined
  >(undefined);
  const [distributionsCreated, setDistributionsCreated] = useState<
    number | undefined
  >(undefined);
  const [completedInterviews, setCompletedInterviews] = useState<
    number | undefined
  >(undefined);
  const [startedInterviews, setStartedInterviews] = useState<
    number | undefined
  >(undefined);

  const handleCustomDateChange = async () => {
    if (customStartDate && customEndDate && customStartDate < customEndDate) {
      const adjustedStartDate = new Date(customStartDate);
      adjustedStartDate.setHours(0, 0, 0, 0);

      const adjustedEndDate = new Date(customEndDate);
      adjustedEndDate.setHours(23, 59, 59, 999);

      setStartDate(adjustedStartDate);
      setEndDate(adjustedEndDate);

      const diagnosticsCreatedCount = await getTotalDiagnosticsCreated({
        startDate: adjustedStartDate,
        endDate: adjustedEndDate,
      });
      setDiagnosticsCreated(diagnosticsCreatedCount);

      const objectivesCreatedCount = await getTotalObjectivesCreated({
        startDate: adjustedStartDate,
        endDate: adjustedEndDate,
      });
      setObjectivesCreated(objectivesCreatedCount);

      const distributionsCreatedCount = await getTotalDistributionsCreated({
        startDate: adjustedStartDate,
        endDate: adjustedEndDate,
      });
      setDistributionsCreated(distributionsCreatedCount);

      const totalStartedInterviewsCount = await getStartedInterviews({
        startDate: adjustedStartDate,
        endDate: adjustedEndDate,
      });
      setStartedInterviews(totalStartedInterviewsCount);

      const totalCompletedInterviewsCount = await getCompletedInterviews({
        startDate: adjustedStartDate,
        endDate: adjustedEndDate,
      });
      setCompletedInterviews(totalCompletedInterviewsCount);

      setCustomDateError(null);
    } else {
      setCustomDateError("Start date must be earlier than end date.");
    }
  };

  const handleDateChange = async (
    date: Date,
    rangeType: string,
  ): Promise<void> => {
    setSelectedDate(date);

    const { startDate, endDate } = getStartAndEndOfPeriod(date, rangeType);
    setStartDate(startDate);
    setEndDate(endDate);

    const diagnosticsCreatedCount = await getTotalDiagnosticsCreated({
      startDate,
      endDate,
    });
    setDiagnosticsCreated(diagnosticsCreatedCount);

    const objectivesCreatedCount = await getTotalObjectivesCreated({
      startDate,
      endDate,
    });
    setObjectivesCreated(objectivesCreatedCount);

    const distributionsCreatedCount = await getTotalDistributionsCreated({
      startDate,
      endDate,
    });
    setDistributionsCreated(distributionsCreatedCount);

    const totalStartedInterviewsCount = await getStartedInterviews({
      startDate,
      endDate,
    });
    setStartedInterviews(totalStartedInterviewsCount);

    const totalCompletedInterviewsCount = await getCompletedInterviews({
      startDate,
      endDate,
    });
    setCompletedInterviews(totalCompletedInterviewsCount);
  };

  const handleRangeTypeChange = (newRangeType: string) => {
    setDateRangeType(newRangeType);

    if (selectedDate && newRangeType !== "Custom") {
      handleDateChange(selectedDate, newRangeType);
    }
  };

  const overallMetricsForExport = {
    chartTitle: "AI Interviews Metrics",
    lineChartData: [
      {
        legend: "Diagnostics Created",
        data: [
          {
            x: `${startDate?.toISOString()?.substring(0, 10)} - ${endDate?.toISOString()?.substring(0, 10)}`,
            y: diagnosticsCreated,
          },
        ],
      },
      {
        legend: "Objectives Created",
        data: [
          {
            x: `${startDate?.toISOString()?.substring(0, 10)} - ${endDate?.toISOString()?.substring(0, 10)}`,
            y: objectivesCreated,
          },
        ],
      },
      {
        legend: "Distributions Created",
        data: [
          {
            x: `${startDate?.toISOString()?.substring(0, 10)} - ${endDate?.toISOString()?.substring(0, 10)}`,
            y: distributionsCreated,
          },
        ],
      },
      {
        legend: "All Interviews",
        data: [
          {
            x: `${startDate?.toISOString()?.substring(0, 10)} - ${endDate?.toISOString()?.substring(0, 10)}`,
            y: startedInterviews,
          },
        ],
      },
      {
        legend: "Completed Interviews",
        data: [
          {
            x: `${startDate?.toISOString()?.substring(0, 10)} - ${endDate?.toISOString()?.substring(0, 10)}`,
            y: completedInterviews,
          },
        ],
      },
    ],
  };

  return (
    <>
      <div className={headerClasses.root}>
        <div className={headerClasses.titleRow}>
          <h1 className={headerClasses.title}>{pageTitle}</h1>
        </div>
      </div>

      <Stack
        tokens={setGapBetweenHeadersAndDetailsList}
        className={styles.container}
        verticalAlign="start"
        horizontalAlign="start"
      >
        <div className={styles.miniContainer}>
          <DateRangeSelector
            dateRangeType={dateRangeType}
            selectedDate={selectedDate}
            customStartDate={customStartDate}
            customEndDate={customEndDate}
            customDateError={customDateError}
            startDate={startDate}
            endDate={endDate}
            onDateChange={handleDateChange}
            onRangeTypeChange={handleRangeTypeChange}
            onCustomStartDateChange={setCustomStartDate}
            onCustomEndDateChange={setCustomEndDate}
            onCustomDateApply={handleCustomDateChange}
          />
        </div>

        <Separator />

        <ChartExportToCSV
          lineChartData={overallMetricsForExport}
          columns={["Date", "UserActivity", "Metric"]}
        />

        <div className={styles.cardContainer}>
          <Card className={styles.card}>
            <CardHeader
              header={
                <Stack horizontal tokens={{ childrenGap: 12 }}>
                  <Form20Regular className={styles.metricIcon} />
                  <Text size={300} weight="semibold">
                    Diagnostics Created
                  </Text>
                </Stack>
              }
              action={
                <Tooltip
                  content={
                    "Total number of diagnostics created in the selected period."
                  }
                  relationship={"label"}
                >
                  <Info24Regular className={styles.icon} />
                </Tooltip>
              }
            />
            <div className={styles.cardContent}>
              <Text size={600} weight="bold">
                {diagnosticsCreated !== undefined ? diagnosticsCreated : "--"}
              </Text>
            </div>
          </Card>

          <Card className={styles.card}>
            <CardHeader
              header={
                <Stack horizontal tokens={{ childrenGap: 12 }}>
                  <TargetAddRegular className={styles.metricIcon} />
                  <Text size={300} weight="semibold">
                    Objectives Created
                  </Text>
                </Stack>
              }
              action={
                <Tooltip
                  content={
                    "Total number of objectives created in the selected period."
                  }
                  relationship={"label"}
                >
                  <Info24Regular className={styles.icon} />
                </Tooltip>
              }
            />
            <div className={styles.cardContent}>
              <Text size={600} weight="bold">
                {objectivesCreated !== undefined ? objectivesCreated : "--"}
              </Text>
            </div>
          </Card>

          <Card className={styles.card}>
            <CardHeader
              header={
                <Stack horizontal tokens={{ childrenGap: 12 }}>
                  <FlowchartRegular className={styles.metricIcon} />
                  {/* <DataHistogramRegular className={styles.metricIcon} /> */}
                  <Text size={300} weight="semibold">
                    Distributions Created
                  </Text>
                </Stack>
              }
              action={
                <Tooltip
                  content={
                    "Total number of distributions created in the selected period."
                  }
                  relationship={"label"}
                >
                  <Info24Regular className={styles.icon} />
                </Tooltip>
              }
            />
            <div className={styles.cardContent}>
              <Text size={600} weight="bold">
                {distributionsCreated !== undefined
                  ? distributionsCreated
                  : "--"}
              </Text>
            </div>
          </Card>

          <Card className={styles.card}>
            <CardHeader
              header={
                <Stack horizontal tokens={{ childrenGap: 12 }}>
                  <CalendarPlayRegular className={styles.metricIcon} />
                  <Text size={300} weight="semibold">
                    All Interviews
                  </Text>
                </Stack>
              }
              action={
                <Tooltip
                  content={
                    "Total number of completed and incomplete interviews in the selected period."
                  }
                  relationship={"label"}
                >
                  <Info24Regular className={styles.icon} />
                </Tooltip>
              }
            />
            <div className={styles.cardContent}>
              <Text size={600} weight="bold">
                {startedInterviews !== undefined ? startedInterviews : "--"}
              </Text>
            </div>
          </Card>

          <Card className={styles.card}>
            <CardHeader
              header={
                <Stack horizontal tokens={{ childrenGap: 12 }}>
                  <CalendarCheckmarkRegular className={styles.metricIcon} />
                  <Text size={300} weight="semibold">
                    Completed Interviews
                  </Text>
                </Stack>
              }
              action={
                <Tooltip
                  content={
                    "Total number of completed interviews in the selected period."
                  }
                  relationship={"label"}
                >
                  <Info24Regular className={styles.icon} />
                </Tooltip>
              }
            />
            <div className={styles.cardContent}>
              <Text size={600} weight="bold">
                {completedInterviews !== undefined ? completedInterviews : "--"}
              </Text>
            </div>
          </Card>
        </div>
      </Stack>
    </>
  );
};
